import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useSocket } from "./SocketContext";
import { useUser } from "./UserContext";
import { useNotification } from "./NotificationContext";
import { chatAPI } from "../utils/chatApi";

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { socket, isUserOnline, onlineUserIds } = useSocket();
  const { user: currentUser } = useUser();
  const { showToast } = useNotification();

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("recent"); // "recent" | "people"
  const [activeContact, setActiveContact] = useState(null);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [projectContacts, setProjectContacts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [otherLastReadAt, setOtherLastReadAt] = useState(null);
  const [isOtherTyping, setIsOtherTyping] = useState(false);

  const activeConvIdRef = useRef(null);
  activeConvIdRef.current = activeConversationId;

  const activeContactRef = useRef(null);
  activeContactRef.current = activeContact;

  const isOpenRef = useRef(false);
  isOpenRef.current = isOpen;

  // Fetch initial unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!currentUser) return;
    try {
      const res = await chatAPI.getUnreadCount();
      if (res.success) {
        setUnreadCount(res.count || 0);
      }
    } catch (err) {
      console.warn("Failed to fetch chat unread count:", err);
    }
  }, [currentUser]);

  // Fetch recent conversations
  const fetchConversations = useCallback(async () => {
    if (!currentUser) return;
    try {
      const res = await chatAPI.getConversations();
      if (res.success) {
        setConversations(res.conversations || []);
      }
    } catch (err) {
      console.warn("Failed to fetch chat conversations:", err);
    }
  }, [currentUser]);

  // Fetch team contacts
  const fetchContacts = useCallback(async (projectId) => {
    if (!currentUser) return;
    try {
      const res = await chatAPI.getContacts(projectId ? { projectId } : undefined);
      if (res.success) {
        setContacts(res.contacts || []);
        if (Array.isArray(res.projects)) {
          setProjectContacts(res.projects);
        }
      }
    } catch (err) {
      console.warn("Failed to fetch chat contacts:", err);
    }
  }, [currentUser]);

  // Initial load when user logs in
  useEffect(() => {
    if (currentUser) {
      fetchUnreadCount();
      fetchConversations();
      fetchContacts();
    }
  }, [currentUser, fetchUnreadCount, fetchConversations, fetchContacts]);

  // Load messages for a conversation
  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    setLoadingMessages(true);
    try {
      const res = await chatAPI.getMessages(convId);
      if (res.success) {
        setMessages(res.messages || []);
        setOtherLastReadAt(res.otherLastReadAt || null);
        // Mark as read immediately and sync count
        chatAPI.markAsRead(convId).then((readRes) => {
          if (typeof readRes?.unreadCount === "number") {
            setUnreadCount(readRes.unreadCount);
          } else {
            fetchUnreadCount();
          }
          fetchConversations();
        }).catch(() => {});
      }
    } catch (err) {
      console.error("Failed to load messages:", err);
    } finally {
      setLoadingMessages(false);
    }
  }, [fetchUnreadCount, fetchConversations]);

  // Open chat with a specific user (from People list)
  const openChatWithUser = useCallback(
    (contact) => {
      const contactId = String(contact.id || contact._id || "");
      setActiveContact(contact);
      activeContactRef.current = contact;
      setIsOpen(true);
      isOpenRef.current = true;

      // Check if conversation already exists in cached list
      const existing = conversations.find(
        (c) => String(c.with?.id || c.with?._id || "") === contactId
      );

      if (existing) {
        const convId = String(existing.id || existing._id);
        setActiveConversationId(convId);
        activeConvIdRef.current = convId;
        // Optimistically clear unread count for this thread
        if (existing.unread > 0) {
          setUnreadCount((c) => Math.max(0, c - (existing.unread || 1)));
          setConversations((prev) =>
            prev.map((c) =>
              String(c.id || c._id) === convId ? { ...c, unread: 0 } : c
            )
          );
        }
        loadMessages(convId);
      } else {
        setActiveConversationId(null);
        activeConvIdRef.current = null;
        setMessages([]);
        setOtherLastReadAt(null);
      }
    },
    [conversations, loadMessages]
  );

  // Open existing conversation (from Recent list)
  const openConversation = useCallback(
    (conv) => {
      const convId = String(conv.id || conv._id);
      setActiveContact(conv.with);
      activeContactRef.current = conv.with;
      setActiveConversationId(convId);
      activeConvIdRef.current = convId;
      setIsOpen(true);
      isOpenRef.current = true;
      // Optimistically clear unread count for this thread
      if (conv.unread > 0) {
        setUnreadCount((c) => Math.max(0, c - (conv.unread || 1)));
        setConversations((prev) =>
          prev.map((c) =>
            String(c.id || c._id) === convId ? { ...c, unread: 0 } : c
          )
        );
      }
      loadMessages(convId);
    },
    [loadMessages]
  );

  // Return from conversation thread to conversation/people list
  const backToList = useCallback(() => {
    setActiveContact(null);
    setActiveConversationId(null);
    setMessages([]);
    setIsOtherTyping(false);
    fetchConversations();
  }, [fetchConversations]);

  // Close the chat window completely
  const closeChat = useCallback(() => {
    setIsOpen(false);
    setIsOtherTyping(false);
  }, []);

  // Send a message (real-time optimistic + API)
  const sendMessage = useCallback(
    async ({ body, attachments = [] }) => {
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const recipientId = activeContactRef.current?.id || activeContactRef.current?._id;
      const convId = activeConvIdRef.current;

      if (!recipientId && !convId) return;

      const optimisticMsg = {
        id: tempId,
        _id: tempId,
        tempId,
        conversationId: convId,
        senderId: currentUser?._id,
        recipientId,
        body: body?.trim() || "",
        attachments: attachments || [],
        mine: true,
        read: false,
        pending: true,
        createdAt: new Date().toISOString(),
      };

      // Optimistically show message in thread immediately
      setMessages((prev) => [...prev, optimisticMsg]);

      // Optimistically update conversation list snippet in real-time
      if (convId) {
        setConversations((prev) => {
          const idx = prev.findIndex((c) => String(c.id || c._id) === String(convId));
          if (idx >= 0) {
            const updated = {
              ...prev[idx],
              unread: 0,
              lastMessage: {
                id: tempId,
                body: optimisticMsg.body,
                preview: optimisticMsg.body || (attachments.length ? `📎 ${attachments[0].fileName}` : ""),
                mine: true,
                createdAt: optimisticMsg.createdAt,
                read: false,
              },
              lastMessageAt: optimisticMsg.createdAt,
            };
            return [updated, ...prev.filter((_, i) => i !== idx)];
          }
          return prev;
        });
      }

      try {
        const res = await chatAPI.sendMessage({
          recipientId,
          conversationId: convId,
          body,
          attachments,
          tempId,
        });

        if (res.success && res.message) {
          const realMsg = res.message;
          const realId = realMsg.id || realMsg._id;

          // Replace tempId with confirmed message (or remove tempId if socket already added it)
          setMessages((prev) => {
            const hasReal = prev.some(
              (m) => (m._id && m._id === realId) || (m.id && m.id === realId)
            );
            if (hasReal) {
              return prev.filter((m) => m.id !== tempId && m._id !== tempId);
            }
            return prev.map((m) =>
              m.id === tempId || m._id === tempId ? { ...realMsg, mine: true } : m
            );
          });

          if (!convId && res.conversationId) {
            const newConvId = String(res.conversationId);
            setActiveConversationId(newConvId);
            activeConvIdRef.current = newConvId;
          }

          fetchConversations();
        }
      } catch (err) {
        console.error("Failed to send message:", err);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempId ? { ...m, failed: true, pending: false } : m
          )
        );
      }
    },
    [currentUser, fetchConversations]
  );

  // Socket.IO event listeners for full real-time communication
  useEffect(() => {
    if (!socket || !currentUser) return;

    // Incoming new message from another user
    const handleIncomingMessage = (data) => {
      const { message, conversationId, sender } = data;
      const convIdStr = String(conversationId || message?.conversationId || "");
      const activeConvIdStr = String(activeConvIdRef.current || "");
      const senderIdStr = String(sender?.id || sender?._id || message?.senderId || "");
      const activeContactIdStr = String(
        activeContactRef.current?.id || activeContactRef.current?._id || ""
      );

      const isCurrentThread =
        (Boolean(convIdStr) && Boolean(activeConvIdStr) && activeConvIdStr === convIdStr) ||
        (Boolean(senderIdStr) && Boolean(activeContactIdStr) && activeContactIdStr === senderIdStr);

      if (isCurrentThread) {
        if (!activeConvIdRef.current && conversationId) {
          const newConvId = String(conversationId);
          setActiveConversationId(newConvId);
          activeConvIdRef.current = newConvId;
        }

        setMessages((prev) => {
          const realId = String(message.id || message._id || "");
          if (prev.some((m) => String(m._id || m.id) === realId)) {
            return prev;
          }
          const isMine = String(sender?.id || sender?._id || message?.senderId || "") === String(currentUser?._id);
          return [...prev, { ...message, mine: isMine }];
        });

        // If the chat panel is open and this thread is in view:
        if (isOpenRef.current) {
          // Immediately mark as read on the backend and sync unread count to 0
          chatAPI
            .markAsRead(conversationId || convIdStr)
            .then((res) => {
              if (typeof res?.unreadCount === "number") {
                setUnreadCount(res.unreadCount);
              } else {
                fetchUnreadCount();
              }
              fetchConversations();
            })
            .catch(() => {});
        } else {
          // Chat is closed/minimized: increment unread badge and show toast
          setUnreadCount((c) => c + 1);
          showToast(
            `💬 ${sender?.name || "Team member"}: ${message.body || "Sent an attachment"}`,
            "info"
          );
        }
      } else {
        // Message is for a different conversation: increment badge & toast
        setUnreadCount((c) => c + 1);
        showToast(
          `💬 ${sender?.name || "Team member"}: ${message.body || "Sent an attachment"}`,
          "info"
        );
      }

      // Update conversations list in real-time immediately
      setConversations((prev) => {
        const idx = prev.findIndex(
          (c) => String(c.id || c._id) === convIdStr
        );
        const lastMsgPreview =
          message.body ||
          (message.attachments?.length ? `📎 ${message.attachments[0].fileName}` : "");

        if (idx >= 0) {
          const updated = {
            ...prev[idx],
            lastMessage: {
              id: message._id || message.id,
              body: message.body,
              preview: lastMsgPreview,
              mine: false,
              createdAt: message.createdAt,
              read: isCurrentThread && isOpenRef.current,
            },
            lastMessageAt: message.createdAt,
            unread:
              isCurrentThread && isOpenRef.current
                ? 0
                : (prev[idx].unread || 0) + 1,
          };
          return [updated, ...prev.filter((_, i) => i !== idx)];
        }
        return prev;
      });

      // Background re-fetch to ensure full sync
      fetchConversations();
    };

    // Confirmation of message sent from another tab or device
    const handleMessageSent = (data) => {
      const { message, conversationId, tempId } = data;
      const convIdStr = String(conversationId || message?.conversationId || "");
      const activeConvIdStr = String(activeConvIdRef.current || "");
      const recipientIdStr = String(message.recipientId || "");
      const activeContactIdStr = String(
        activeContactRef.current?.id || activeContactRef.current?._id || ""
      );

      const isCurrentThread =
        (Boolean(convIdStr) && Boolean(activeConvIdStr) && activeConvIdStr === convIdStr) ||
        (Boolean(recipientIdStr) && Boolean(activeContactIdStr) && activeContactIdStr === recipientIdStr);

      if (isCurrentThread) {
        if (!activeConvIdRef.current && conversationId) {
          setActiveConversationId(conversationId);
          activeConvIdRef.current = conversationId;
        }

        setMessages((prev) => {
          const realId = message.id || message._id;
          // If message is already present, do nothing
          if (prev.some((m) => (m._id && m._id === realId) || (m.id && m.id === realId))) {
            return prev;
          }
          // If we have the tempId message, replace it!
          if (tempId && prev.some((m) => m.id === tempId || m._id === tempId)) {
            return prev.map((m) =>
              m.id === tempId || m._id === tempId ? { ...message, mine: true } : m
            );
          }
          return [...prev, { ...message, mine: true }];
        });
      }

      // Update conversations list in real-time immediately
      setConversations((prev) => {
        const idx = prev.findIndex(
          (c) => String(c.id || c._id) === convIdStr
        );
        const lastMsgPreview =
          message.body ||
          (message.attachments?.length ? `📎 ${message.attachments[0].fileName}` : "");

        if (idx >= 0) {
          const updated = {
            ...prev[idx],
            lastMessage: {
              id: message._id || message.id,
              body: message.body,
              preview: lastMsgPreview,
              mine: true,
              createdAt: message.createdAt,
              read: false,
            },
            lastMessageAt: message.createdAt,
          };
          return [updated, ...prev.filter((_, i) => i !== idx)];
        }
        return prev;
      });

      fetchConversations();
    };

    // Read receipt event
    const handleReadReceipt = (data) => {
      const { conversationId, readAt } = data;
      const convIdStr = String(conversationId || "");
      const activeConvIdStr = String(activeConvIdRef.current || "");
      if (activeConvIdStr && activeConvIdStr === convIdStr) {
        setOtherLastReadAt(readAt);
        setMessages((prev) =>
          prev.map((m) => (m.mine ? { ...m, read: true, readAt } : m))
        );
      }
      fetchConversations();
    };

    // Conversation update notification
    const handleConversationUpdated = (data) => {
      fetchConversations();
      const updatedConvId = String(data?.conversationId || "");
      const activeId = String(activeConvIdRef.current || "");
      if (isOpenRef.current && activeId && updatedConvId && activeId === updatedConvId) {
        chatAPI
          .markAsRead(activeId)
          .then((res) => {
            if (typeof res?.unreadCount === "number") {
              setUnreadCount(res.unreadCount);
            } else {
              fetchUnreadCount();
            }
          })
          .catch(() => {});
      } else {
        fetchUnreadCount();
      }
    };

    // Global unread count broadcast from server
    const handleUnreadCount = (data) => {
      if (typeof data?.count === "number") {
        setUnreadCount(data.count);
      }
    };

    // Typing indicator
    const handleTyping = (data) => {
      const senderIdStr = String(data.senderId || "");
      const activeContactIdStr = String(
        activeContactRef.current?.id || activeContactRef.current?._id || ""
      );
      if (activeContactIdStr && activeContactIdStr === senderIdStr) {
        setIsOtherTyping(true);
      }
    };

    const handleStopTyping = (data) => {
      const senderIdStr = String(data.senderId || "");
      const activeContactIdStr = String(
        activeContactRef.current?.id || activeContactRef.current?._id || ""
      );
      if (activeContactIdStr && activeContactIdStr === senderIdStr) {
        setIsOtherTyping(false);
      }
    };

    socket.on("chat:message", handleIncomingMessage);
    socket.on("chat:message_sent", handleMessageSent);
    socket.on("chat:read", handleReadReceipt);
    socket.on("chat:conversation_updated", handleConversationUpdated);
    socket.on("chat:unread_count", handleUnreadCount);
    socket.on("chat:typing", handleTyping);
    socket.on("chat:stop_typing", handleStopTyping);

    return () => {
      socket.off("chat:message", handleIncomingMessage);
      socket.off("chat:message_sent", handleMessageSent);
      socket.off("chat:read", handleReadReceipt);
      socket.off("chat:conversation_updated", handleConversationUpdated);
      socket.off("chat:unread_count", handleUnreadCount);
      socket.off("chat:typing", handleTyping);
      socket.off("chat:stop_typing", handleStopTyping);
    };
  }, [socket, currentUser, fetchConversations, fetchUnreadCount, showToast]);

  // Real-time polling sync while chat thread is open (fallback to ensure instant sync even if socket disconnects)
  useEffect(() => {
    if (!isOpen || !activeConversationId) return;

    const interval = setInterval(async () => {
      try {
        const res = await chatAPI.getMessages(activeConversationId);
        if (res.success && Array.isArray(res.messages)) {
          setMessages((prev) => {
            const prevIds = new Set(prev.map((m) => String(m.id || m._id)));
            const hasNew = res.messages.some((m) => !prevIds.has(String(m.id || m._id)));
            if (hasNew || res.messages.length !== prev.length) {
              return res.messages;
            }
            return prev;
          });
          if (res.otherLastReadAt) {
            setOtherLastReadAt(res.otherLastReadAt);
          }
        }
      } catch (err) {
        // silent fail
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [isOpen, activeConversationId]);

  // Real-time polling sync for conversations list and unread count when panel is open
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      fetchConversations();
      fetchUnreadCount();
    }, 4000);
    return () => clearInterval(interval);
  }, [isOpen, fetchConversations, fetchUnreadCount]);

  // Send typing emit via socket
  const notifyTyping = useCallback(
    (isTyping) => {
      if (!socket || !activeContact) return;
      const recipientId = activeContact.id || activeContact._id;
      const eventName = isTyping ? "chat:typing" : "chat:stop_typing";
      socket.emit(eventName, {
        recipientId,
        conversationId: activeConversationId,
      });
    },
    [socket, activeContact, activeConversationId]
  );

  const value = {
    isOpen,
    setIsOpen,
    activeTab,
    setActiveTab,
    activeContact,
    activeConversationId,
    messages,
    conversations,
    contacts,
    projectContacts,
    unreadCount,
    setUnreadCount,
    loadingMessages,
    otherLastReadAt,
    isOtherTyping,
    openChatWithUser,
    openConversation,
    backToList,
    closeChat,
    sendMessage,
    notifyTyping,
    fetchConversations,
    fetchContacts,
    fetchUnreadCount,
    isUserOnline,
    onlineUserIds,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
